import { api } from "@/lib/api";
import { OneTimePackage, SubscriptionPlan, AddOn } from "@/types";

export interface PackageApiResponse {
  success: boolean;
  data: any[];
  message?: string;
}

// Transform API package to OneTimePackage
function transformToOneTimePackage(apiPackage: any): OneTimePackage {
  return {
    id: apiPackage.id,
    name: apiPackage.name,
    price: apiPackage.price || 0,
    description: apiPackage.description || "",
    services: apiPackage.services || apiPackage.serviceTypes || apiPackage.features || [],
    active: apiPackage.active !== false,
  };
}

// Transform API package to SubscriptionPlan
function transformToSubscriptionPlan(apiPackage: any): SubscriptionPlan {
  return {
    id: apiPackage.id,
    name: apiPackage.name,
    price: apiPackage.price || 0,
    features: apiPackage.features || [],
    exteriorCleanings:
      apiPackage.exteriorCleanings || apiPackage.exterior_cleanings || 0,
    interiorCleanings:
      apiPackage.interiorCleanings || apiPackage.interior_cleanings || 0,
    active: apiPackage.active !== false,
  };
}

// Transform API package to AddOn
function transformToAddOn(apiPackage: any): AddOn {
  return {
    id: apiPackage.id,
    name: apiPackage.name,
    description: apiPackage.description || "",
    price: apiPackage.price || 0,
    category: apiPackage.category || "special",
    active: apiPackage.active !== false,
  };
}

export const packageService = {
  async getAllPackages(): Promise<any[]> {
    try {
      const response = await api.get<PackageApiResponse>("/packages");
      return Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
    } catch (error) {
      console.error("Error fetching packages:", error);
      throw error;
    }
  },

  async getOneTimePackages(): Promise<OneTimePackage[]> {
    try {
      const packages = await this.getAllPackages();
      // Filter packages by type or assume all are one-time if no type field
      return packages
        .filter(
          (pkg: any) =>
            pkg.type === "PACKAGE" || pkg.type === "one-time" || !pkg.type // If no type field, assume one-time
        )
        .map(transformToOneTimePackage);
    } catch (error) {
      console.error("Error fetching one-time packages:", error);
      throw error;
    }
  },

  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const packages = await this.getAllPackages();
      // Filter packages by type
      return packages
        .filter(
          (pkg: any) =>
            pkg.type === "SUBSCRIPTION" || pkg.type === "subscription"
        )
        .map(transformToSubscriptionPlan);
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
      throw error;
    }
  },

  async getAddOns(): Promise<AddOn[]> {
    try {
      // Try add-ons endpoint first, fallback to packages if needed
      try {
        const response = await api.get<PackageApiResponse>("/add-ons");
        const addOns = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];
        return addOns.map(transformToAddOn);
      } catch {
        // If add-ons endpoint doesn't exist, try to filter from packages
        const packages = await this.getAllPackages();
        return packages
          .filter(
            (pkg: any) => pkg.type === "ADDON" || pkg.category === "addon"
          )
          .map(transformToAddOn);
      }
    } catch (error) {
      console.error("Error fetching add-ons:", error);
      throw error;
    }
  },

  async createPackage(packageData: {
    name: string;
    price: number;
    features: string[];
  }): Promise<OneTimePackage> {
    try {
      const response = await api.post<PackageApiResponse>(
        "/packages",
        packageData
      );
      const createdPackage = Array.isArray(response.data)
        ? response.data[0]
        : response.data.data || response.data;
      return transformToOneTimePackage(createdPackage);
    } catch (error) {
      console.error("Error creating package:", error);
      throw error;
    }
  },

  async updatePackage(id: string, packageData: {
    name: string;
    price: number;
    features: string[];
  }): Promise<OneTimePackage> {
    try {
      const response = await api.put<PackageApiResponse>(
        `/packages/${id}`,
        packageData
      );
      const updatedPackage = Array.isArray(response.data)
        ? response.data[0]
        : response.data.data || response.data;
      return transformToOneTimePackage(updatedPackage);
    } catch (error) {
      console.error("Error updating package:", error);
      throw error;
    }
  },

  async deletePackage(id: string): Promise<void> {
    await api.delete(`/packages/${id}`);
  },
};
